<template>
  <div class="Wdgt-Navigation">
    <CompactAudioplayer class="Wdgt-Navigation__audioplayer"/>
    <div class="Wdgt-Navigation__wrapper">
      <nav class="Wdgt-Navigation__list">
        <div class="Wdgt-Navigation__caret" :style="get_caretStates" :class="{ 'no-transition': isCaretInstant }"></div>
        <button
          class="Wdgt-Navigation__btn"
          :class="buttonActiveClass(get_isNewsSection)"
          @click="activeInfonoise('news')"
          ref="newsBtn"
        >
          <NewsIcon/>
        </button>
        <button
          class="Wdgt-Navigation__btn"
          :class="buttonActiveClass(get_isAlphaSection)"
          @click="activeInfonoise('alpha')"
          ref="alphaBtn"
        >
          <AlphaIcon/>
        </button>
        <button
          class="Wdgt-Navigation__btn"
          :style="isMenuBtnActive ? { color: 'var(--blue-600)' } : {}"
          @click="toggleMenu()"
          ref="menuBtn"
        >
          <component :is="isMenuOpen ? CloseIcon : AppIcon"/>
        </button>
        <transition name="menu-scale">
          <MenuNavigation
            v-if="isMenuOpen"
            class="Wdgt-Navigation__menu-overlay"
            @click.self="isMenuOpen = false"/>
        </transition>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { throttle } from 'lodash';
import { PATHS_NAME } from '@shared/routers/consts/pathsName';
//---------------------------------------
import CompactAudioplayer from '@/Groups/Audioplayer/public/modules/CompactAudioplayer/CompactAudioplayer.vue';
import AlphaIcon from '@svg/navigation/AlphaIcon.vue';
import NewsIcon from "@svg/navigation/NewsIcon.vue";
import AppIcon from "@svg/navigation/AppIcon.vue";
import MenuNavigation from "@widgets/NavigationWidget/components/Menu/MenuNavigation.vue";
import CloseIcon from "@svg/navigation/CloseIcon.vue";
//---------------------------------------
const router = useRouter();
const route = useRoute();
const store = useStore();
//---------------------------------------
const ACTIVE_BTN_CLASS = 'Wdgt-Navigation__btn--active';

const caretWidth = ref(0);
const caretLeft = ref(0);
const activeTag = ref(null);

const newsBtn = ref(null);
const alphaBtn = ref(null);
const menuBtn = ref(null);

const isCaretInstant = ref(true);
const isMenuOpen = ref(false);

const isMenuBtnActive = computed(() => activeTag.value === menuBtn.value);
const get_isInfonoisePage = computed(() => route.name === PATHS_NAME.MAIN);
const get_isNewsSection = computed(() => store.state.ContentSystem.isAdaptiveNews && get_isInfonoisePage.value);
const get_isAlphaSection = computed(() => store.state.ContentSystem.isAdaptiveAlpha && get_isInfonoisePage.value);
const get_isCalendarPage = computed(() => route.name === PATHS_NAME.CALENDAR);
const get_isForecastersPage = computed(() => route.name === PATHS_NAME.FORECASTERS);
const get_isSignalsPage = computed(() => route.name === PATHS_NAME.SIGNALS);

const get_caretStates = computed(() => ({
  width: `${caretWidth.value}px`,
  left: `${caretLeft.value}px`
}));

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  nextTick(() => {
    requestAnimationFrame(() => {
      toMoveCaret();
    });
  });
};

const handleResize = throttle(() => {
  toCalcWidthCaret();
  toMoveCaret();
  setTimeout(() => {
    toMoveCaret();
  }, 50);
}, 150);

const buttonActiveClass = (condition) => (condition ? ACTIVE_BTN_CLASS : '');

const toCalcWidthCaret = (retry = 0) => {
  toFindActiveElement();
  if (!activeTag.value || activeTag.value.offsetWidth === 0) {
    if (retry < 5) {
      setTimeout(() => {
        toCalcWidthCaret(retry + 1);
      }, 100);
    }
    return;
  }

  caretWidth.value = activeTag.value.offsetWidth;
};

const toMoveCaret = (retry = 0) => {
  toFindActiveElement();
  if (!activeTag.value) return;

  const relativeParent = activeTag.value.offsetParent;
  const childRect = activeTag.value.getBoundingClientRect();
  const parentRect = relativeParent?.getBoundingClientRect?.() || { left: 0 };

  const left = childRect.left - parentRect.left;
  if (left === 0 && retry < 5) {
    setTimeout(() => {
      toMoveCaret(retry + 1);
    }, 100);
    return;
  }

  caretLeft.value = left;
};

const activeInfonoise = (type) => {
  window.scrollTo({ top: 0 });
  if (type === 'news') store.commit('ContentSystem/toChangeNewsVisible');
  else if (type === 'alpha') store.commit('ContentSystem/toChangeAlphaVisible');

  router.push({ name: PATHS_NAME.MAIN }).then(() => {
    nextTick(() => {
      requestAnimationFrame(() => {
        toCalcWidthCaret();
        toMoveCaret();
      });
    });
  });
};

const toFindActiveElement = () => {
  const candidates = [
    { condition: get_isNewsSection.value, element: newsBtn.value },
    { condition: get_isAlphaSection.value, element: alphaBtn.value },
    { condition: get_isCalendarPage.value, element: menuBtn.value },
    { condition: get_isForecastersPage.value, element: menuBtn.value },
    { condition: get_isSignalsPage.value, element: menuBtn.value },
  ];
  const active = candidates.find(({ condition }) => condition);
  activeTag.value = active ? active.element : null;
};

const handleClickOutsideMenu = (event) => {
  if (
    isMenuOpen.value &&
    !menuBtn.value?.contains(event.target) &&
    !document.querySelector('.Wdgt-Navigation__menu-overlay')?.contains(event.target)
  ) {
    isMenuOpen.value = false;
  }
};

let resizeObserver = null;

onMounted(() => {
  handleResize();
  window.addEventListener('resize', handleResize);
  document.addEventListener('click', handleClickOutsideMenu);

  router.afterEach(() => {
    toCalcWidthCaret();
    nextTick(() => {
      requestAnimationFrame(() => {
        toMoveCaret();
        setTimeout(toMoveCaret, 50);
      });
    });
  });

  // Наблюдение за размерами активных элементов
  resizeObserver = new ResizeObserver(() => {
    toCalcWidthCaret();
    toMoveCaret();
  });

  if (newsBtn.value) resizeObserver.observe(newsBtn.value);
  if (alphaBtn.value) resizeObserver.observe(alphaBtn.value);
  if (menuBtn.value) resizeObserver.observe(menuBtn.value);

  nextTick(() => {
    setTimeout(() => {
      toCalcWidthCaret();
      toMoveCaret();
      isCaretInstant.value = false;
    }, 150);
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  document.removeEventListener('click', handleClickOutsideMenu);
  resizeObserver?.disconnect?.();
});

watch(
  [() => isMenuOpen.value, () => route.fullPath],
  ([menuOpen, path], [prevOpen, prevPath]) => {
    if (path !== prevPath) isMenuOpen.value = false;
    nextTick(() => {
      requestAnimationFrame(() => {
        toMoveCaret();
      });
    });
  },
  { flush: 'post' }
);
</script>

<style lang="scss">
@import "./styles/_styles";


</style>
